"use client";

import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TextArea } from '@/components/ui/TextArea';
import type { ClientPreferences, PreferenceProvenanceEntry } from '@/types/preferences';
import { Select } from '@/components/ui/Select';

type PreferenceField = 'spokesperson' | 'mediaContact' | 'tone' | 'styleNotes' | 'locale' | null;

interface PreferencesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  preferences: ClientPreferences | null;
  provenance?: Record<string, PreferenceProvenanceEntry | undefined>;
  onSave: (updates: Partial<ClientPreferences>) => Promise<void> | void;
  focusField?: PreferenceField;
}

export function PreferencesDrawer({
  isOpen,
  onClose,
  preferences,
  provenance,
  onSave,
  focusField = null,
}: PreferencesDrawerProps) {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [animateIn, setAnimateIn] = useState(false);
  const [saving, setSaving] = useState(false);

  const [spokespersonName, setSpokespersonName] = useState('');
  const [spokespersonTitle, setSpokespersonTitle] = useState('');
  const [spokespersonQuote, setSpokespersonQuote] = useState('');
  const [mediaName, setMediaName] = useState('');
  const [mediaEmail, setMediaEmail] = useState('');
  const [mediaPhone, setMediaPhone] = useState('');
  const [tone, setTone] = useState('');
  const [styleNotesText, setStyleNotesText] = useState('');
  const [locale, setLocale] = useState<'en' | 'ar'>('en');

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setAnimateIn(false);
      const frame = requestAnimationFrame(() => setAnimateIn(true));
      return () => cancelAnimationFrame(frame);
    }
    if (!isOpen && isVisible) {
      setAnimateIn(false);
      const timeout = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isOpen, isVisible]);

  useEffect(() => {
    if (!preferences) {
      setSpokespersonName('');
      setSpokespersonTitle('');
      setSpokespersonQuote('');
      setMediaName('');
      setMediaEmail('');
      setMediaPhone('');
      setTone('');
      setStyleNotesText('');
      setLocale('en');
      return;
    }
    setSpokespersonName(preferences.spokesperson?.name || '');
    setSpokespersonTitle(preferences.spokesperson?.title || '');
    setSpokespersonQuote(preferences.spokesperson?.quote || '');
    setMediaName(preferences.mediaContact?.name || '');
    setMediaEmail(preferences.mediaContact?.email || '');
    setMediaPhone(preferences.mediaContact?.phone || '');
    setTone(preferences.tone?.toString() || '');
    setStyleNotesText((preferences.styleNotes || []).join('\n'));
    setLocale(preferences.locale || 'en');
  }, [preferences]);

  const styleNotes = useMemo(() => {
    return styleNotesText
      .split('\n')
      .map((line) => line.replace(/^[-*•]\s*/, '').trim())
      .filter(Boolean);
  }, [styleNotesText]);

  const provenanceRows = useMemo(() => {
    if (!provenance) return [] as Array<{ field: string; source: string; confidence: number }>;
    return Object.entries(provenance)
      .filter(([, value]) => !!value)
      .map(([field, value]) => ({
        field,
        source: (value?.source || 'unknown').replace(/_/g, ' '),
        confidence: typeof value?.confidence === 'number' ? value.confidence : 0,
      }))
      .slice(0, 6);
  }, [provenance]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Partial<ClientPreferences> = {
        ...(spokespersonName
          ? {
              spokesperson: {
                name: spokespersonName.trim(),
                ...(spokespersonTitle.trim() ? { title: spokespersonTitle.trim() } : {}),
                ...(spokespersonQuote.trim() ? { quote: spokespersonQuote.trim() } : {}),
                updatedAt: new Date().toISOString(),
              },
            }
          : {}),
        ...(mediaName
          ? {
              mediaContact: {
                name: mediaName.trim(),
                ...(mediaEmail.trim() ? { email: mediaEmail.trim() } : {}),
                ...(mediaPhone.trim() ? { phone: mediaPhone.trim() } : {}),
                updatedAt: new Date().toISOString(),
              },
            }
          : {}),
        ...(tone.trim() ? { tone: tone.trim() } : {}),
        ...(styleNotes.length ? { styleNotes } : {}),
        ...(locale ? { locale } : {}),
      };
      await onSave(updates);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 bg-black/50 z-40 backdrop-blur-sm transition-opacity duration-200',
          animateIn ? 'opacity-100' : 'opacity-0',
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        )}
        onClick={() => {
          if (!saving) onClose();
        }}
      />

      <div
        className={cn(
          'fixed inset-y-0 right-0 w-full sm:w-[420px] bg-background shadow-2xl z-50 flex flex-col',
          'transition-transform duration-200 ease-out',
          animateIn ? 'translate-x-0' : 'translate-x-full',
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Client preferences"
      >
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Client preferences</h2>
            <p className="text-xs text-muted-foreground">Update defaults for future drafts.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close preferences">
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {provenanceRows.length > 0 && (
            <div className="rounded-xl border border-border/60 bg-muted/30 p-3">
              <h3 className="text-sm font-semibold text-foreground">Why this style</h3>
              <p className="text-xs text-muted-foreground mt-1">Current effective sources for this conversation.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {provenanceRows.map((entry) => (
                  <span key={`${entry.field}-${entry.source}`} className="text-[11px] px-2 py-1 rounded-full border border-border/60 text-muted-foreground">
                    {entry.field}: {entry.source} ({entry.confidence.toFixed(2)})
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Spokesperson</h3>
            <Input
              label="Name"
              value={spokespersonName}
              onChange={(e) => setSpokespersonName(e.target.value)}
              autoFocus={focusField === 'spokesperson'}
              placeholder="e.g., Mara Chen"
            />
            <Input
              label="Title"
              value={spokespersonTitle}
              onChange={(e) => setSpokespersonTitle(e.target.value)}
              placeholder="e.g., VP of Product"
            />
            <TextArea
              label="Quote"
              value={spokespersonQuote}
              onChange={(e) => setSpokespersonQuote(e.target.value)}
              textareaSize="sm"
              placeholder="Optional quote or preferred phrasing"
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Media contact</h3>
            <Input
              label="Name"
              value={mediaName}
              onChange={(e) => setMediaName(e.target.value)}
              autoFocus={focusField === 'mediaContact'}
              placeholder="e.g., Sara Ali"
            />
            <Input
              label="Email"
              value={mediaEmail}
              onChange={(e) => setMediaEmail(e.target.value)}
              placeholder="press@example.com"
              type="email"
            />
            <Input
              label="Phone"
              value={mediaPhone}
              onChange={(e) => setMediaPhone(e.target.value)}
              placeholder="+1 555-123-4567"
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Tone</h3>
            <Input
              label="Preferred tone"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              autoFocus={focusField === 'tone'}
              placeholder="optimistic, neutral, cautious"
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Style notes</h3>
            <TextArea
              label="Notes (one per line)"
              value={styleNotesText}
              onChange={(e) => setStyleNotesText(e.target.value)}
              autoFocus={focusField === 'styleNotes'}
              textareaSize="md"
              placeholder="Use AP style.\nEmphasize localization and regional impact."
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Locale</h3>
            <label className="text-xs text-muted-foreground">Default locale</label>
            <Select
              value={locale}
              onChange={(e) => setLocale(e.target.value as 'en' | 'ar')}
              size="sm"
            >
              <option value="en">English</option>
              <option value="ar">Arabic</option>
            </Select>
          </div>
        </div>

        <div className="p-4 border-t border-border/60 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save preferences
          </Button>
        </div>
      </div>
    </>
  );
}
